import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MdDelete, MdPlaylistAdd } from "react-icons/md";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import {
  Utils,
  fetchWithAuth,
  fetchApplicationAppId,
  can,
  PARTNER_USERS_FROM,
  PARTNER_IN_APP_WINDOW_UNIT,
  PARTNER_INCLUDE_VALUES,
  PARTNER_TYPE,
  PartnerLogo,
  selectStyles,
} from "../../utils";

const emptyCondition = () => ({ property: "", value: "", map_to: "" });

const emptyMapRow = () => ({
  ns_event: "",
  partner_event: "",
  users_from: 1,
  include_values: 0,
  status: 1,
  conditions: [],
  showExtra: false,
});

function normalizeMapRow(m) {
  const conditions = Array.isArray(m?.conditions)
    ? m.conditions.map((c) => ({
        property: String(c?.property || ""),
        value: String(c?.value || ""),
        map_to: String(c?.map_to || ""),
      }))
    : [];
  return {
    ns_event: m.ns_event || "",
    partner_event: m.partner_event || "",
    users_from: Number(m.users_from) === 2 ? 2 : 1,
    include_values: [0, 1, 2].includes(Number(m.include_values))
      ? Number(m.include_values)
      : 0,
    status: Number(m.status) === 0 ? 0 : 1,
    conditions,
    showExtra: conditions.length > 0,
  };
}

function partnerEventOptionsFromConfig(partner) {
  const raw = partner?.default_config?.partner_events;
  const list = Array.isArray(raw)
    ? raw
    : String(raw || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
  const seen = new Set();
  const options = [];
  list.forEach((item) => {
    const value = typeof item === "object" ? String(item.value || item.label || "") : String(item);
    const label =
      typeof item === "object" ? String(item.label || item.value || "") : String(item);
    if (!value || seen.has(value)) return;
    seen.add(value);
    options.push({ value, label });
  });
  return options;
}

const mapSelectStyles = {
  ...selectStyles,
  control: (base, state) => ({
    ...selectStyles.control(base, state),
    minHeight: 34,
    height: 34,
    borderRadius: 6,
    fontSize: 13,
  }),
  valueContainer: (base) => ({
    ...base,
    height: 32,
    padding: "0 8px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 32,
  }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  clearIndicator: (base) => ({ ...base, padding: 4 }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  menuPortal: (base) => ({ ...base, zIndex: 10020 }),
};

export default function CollaboratePartnerDetail() {
  const { appId: routeAppId, partnerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "about" ? "about" : "integration";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appUuid, setAppUuid] = useState(null);
  const [partner, setPartner] = useState(null);
  const [knownEvents, setKnownEvents] = useState([]);
  const [dirty, setDirty] = useState(false);

  const [status, setStatus] = useState(0);
  const [configValues, setConfigValues] = useState({});
  const [installEnabled, setInstallEnabled] = useState(1);
  const [installUsersFrom, setInstallUsersFrom] = useState(1);
  const [inAppEnabled, setInAppEnabled] = useState(0);
  const [windowUnit, setWindowUnit] = useState(3);
  const [windowValue, setWindowValue] = useState(6);
  const [eventMaps, setEventMaps] = useState([]);
  const [selectedMapIdxs, setSelectedMapIdxs] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkUsersFrom, setBulkUsersFrom] = useState(2);
  const [bulkInclude, setBulkInclude] = useState(0);
  const bulkRef = useRef(null);

  const canManage = can("collaborate.partner_integrations.manage");

  const partnerEventPresets = useMemo(
    () => partnerEventOptionsFromConfig(partner),
    [partner]
  );

  const nsEventOptions = useMemo(() => {
    const base = (knownEvents || []).map((ev) => ({ value: String(ev), label: String(ev) }));
    const seen = new Set(base.map((o) => o.value));
    (eventMaps || []).forEach((row) => {
      const value = String(row.ns_event || "").trim();
      if (value && !seen.has(value)) {
        seen.add(value);
        base.push({ value, label: value });
      }
    });
    return base;
  }, [knownEvents, eventMaps]);

  const partnerEventOptions = useMemo(() => {
    const base = [...partnerEventPresets];
    const seen = new Set(base.map((o) => o.value));
    (eventMaps || []).forEach((row) => {
      if (row.partner_event) {
        const value = String(row.partner_event).trim();
        if (value && !seen.has(value)) {
          seen.add(value);
          base.push({ value, label: value });
        }
      }
      (row.conditions || []).forEach((c) => {
        const value = String(c.map_to || "").trim();
        if (value && !seen.has(value)) {
          seen.add(value);
          base.push({ value, label: value });
        }
      });
    });
    return base;
  }, [partnerEventPresets, eventMaps]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const uuid = await fetchApplicationAppId(routeAppId);
      if (!cancelled) setAppUuid(uuid || routeAppId);
    })();
    return () => {
      cancelled = true;
    };
  }, [routeAppId]);

  useEffect(() => {
    if (!appUuid || !partnerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(Utils.baseurl + "get/partner-integration", {
          method: "POST",
          body: JSON.stringify({ app_id: appUuid, slug: partnerId, partner_id: partnerId }),
        });
        const result = await response.json();
        if (cancelled) return;
        if (!result.status) {
          toast.error(result.message || "Partner not found");
          setPartner(null);
          setLoading(false);
          return;
        }
        const p = result.data.partner;
        const integ = result.data.integration || {};
        setPartner(p);
        setKnownEvents(result.data.known_events || []);
        setStatus(Number(integ.status) === 1 ? 1 : 0);
        setConfigValues(
          integ.config_values && typeof integ.config_values === "object" && !Array.isArray(integ.config_values)
            ? { ...integ.config_values }
            : {}
        );
        setInstallEnabled(Number(integ.install_postback_enabled) === 0 ? 0 : 1);
        setInstallUsersFrom(Number(integ.install_users_from) === 2 ? 2 : 1);
        setInAppEnabled(Number(integ.in_app_postbacks_enabled) === 1 ? 1 : 0);
        {
          const loadedUnit = Number(integ.in_app_window_unit) || 3;
          const loadedVal = Number(integ.in_app_window_value) || 6;
          const maxForUnit =
            loadedUnit === 1 ? 23 : loadedUnit === 2 ? 30 : loadedUnit === 3 ? 12 : 1;
          setWindowUnit(loadedUnit);
          setWindowValue(
            loadedUnit === 4 ? loadedVal : Math.min(Math.max(1, loadedVal), maxForUnit)
          );
        }
        setEventMaps((integ.event_maps || []).map(normalizeMapRow));
        setSelectedMapIdxs([]);
        setDirty(false);
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          toast.error("Failed to load partner");
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [appUuid, partnerId]);

  function markDirty(updater) {
    updater();
    setDirty(true);
  }

  function setTab(next) {
    const params = new URLSearchParams(searchParams);
    if (next === "about") params.set("tab", "about");
    else params.delete("tab");
    setSearchParams(params, { replace: true });
  }

  async function handleSave() {
    if (!canManage) {
      toast.error("You do not have permission to manage integrations");
      return;
    }
    setSaving(true);
    try {
      const response = await fetchWithAuth(Utils.baseurl + "save/partner-integration", {
        method: "POST",
        body: JSON.stringify({
          app_id: appUuid,
          partner_id: partner.id,
          status,
          config_values: configValues,
          install_postback_enabled: status === 1 ? 1 : 0,
          install_users_from: installUsersFrom,
          in_app_postbacks_enabled: inAppEnabled,
          in_app_window_unit: windowUnit,
          in_app_window_value: windowValue,
          event_maps: eventMaps.map((m) => ({
            ns_event: m.ns_event,
            partner_event: m.showExtra && (m.conditions || []).some((c) => c.map_to)
              ? String((m.conditions || []).find((c) => c.map_to)?.map_to || m.partner_event || "")
              : m.partner_event,
            users_from: m.users_from,
            include_values: m.include_values,
            status: m.status,
            conditions: m.showExtra ? m.conditions || [] : [],
          })),
        }),
      });
      const result = await response.json();
      if (result.status) {
        toast.success(result.message || "Integration saved");
        setDirty(false);
        const integ = result.data || {};
        setStatus(Number(integ.status) === 1 ? 1 : 0);
        setConfigValues(
          integ.config_values && typeof integ.config_values === "object" && !Array.isArray(integ.config_values)
            ? { ...integ.config_values }
            : {}
        );
        setInstallEnabled(Number(integ.install_postback_enabled) === 0 ? 0 : 1);
        setInstallUsersFrom(Number(integ.install_users_from) === 2 ? 2 : 1);
        setInAppEnabled(Number(integ.in_app_postbacks_enabled) === 1 ? 1 : 0);
        {
          const loadedUnit = Number(integ.in_app_window_unit) || 3;
          const loadedVal = Number(integ.in_app_window_value) || 6;
          const maxForUnit =
            loadedUnit === 1 ? 23 : loadedUnit === 2 ? 30 : loadedUnit === 3 ? 12 : 1;
          setWindowUnit(loadedUnit);
          setWindowValue(
            loadedUnit === 4 ? loadedVal : Math.min(Math.max(1, loadedVal), maxForUnit)
          );
        }
        setEventMaps((integ.event_maps || []).map(normalizeMapRow));
        setSelectedMapIdxs([]);
      } else {
        toast.error(result.message || "Save failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    }
    setSaving(false);
  }

  function discardChanges() {
    window.location.reload();
  }

  function windowUnitMax(unit) {
    if (Number(unit) === 1) return 23; // Hours
    if (Number(unit) === 2) return 30; // Days
    if (Number(unit) === 3) return 12; // Months
    return 1;
  }

  function windowTickLabels(unit) {
    const u = Number(unit);
    if (u === 1) return [1, 4, 8, 12, 16, 20, 23];
    if (u === 2) return [1, 5, 10, 15, 20, 25, 30];
    if (u === 3) return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    return [];
  }

  function windowUnitLabel(unit, value) {
    const u = Number(unit);
    const n = Number(value) || 0;
    if (u === 1) return `${n} hour${n === 1 ? "" : "s"}`;
    if (u === 2) return `${n} day${n === 1 ? "" : "s"}`;
    if (u === 3) return `${n} month${n === 1 ? "" : "s"}`;
    return "Lifetime";
  }

  function setWindowUnitSafe(nextUnit) {
    markDirty(() => {
      setWindowUnit(nextUnit);
      const max = windowUnitMax(nextUnit);
      setWindowValue((prev) => Math.min(Math.max(1, Number(prev) || 1), max));
    });
  }

  function onActivateChange(checked) {
    markDirty(() => {
      setStatus(checked ? 1 : 0);
      if (checked) setInstallEnabled(1);
    });
  }

  function toggleMapSelected(idx) {
    setSelectedMapIdxs((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  }

  function toggleSelectAllMaps() {
    if (!eventMaps.length) return;
    setSelectedMapIdxs((prev) =>
      prev.length === eventMaps.length ? [] : eventMaps.map((_, i) => i)
    );
  }

  function applyBulkActions() {
    if (!selectedMapIdxs.length) {
      toast.error("Select at least one event row");
      return;
    }
    markDirty(() => {
      setEventMaps((prev) =>
        prev.map((row, idx) =>
          selectedMapIdxs.includes(idx)
            ? { ...row, users_from: bulkUsersFrom, include_values: bulkInclude }
            : row
        )
      );
    });
    setBulkOpen(false);
    toast.success("Bulk settings applied");
  }

  function toggleExtraFields(idx) {
    markDirty(() => {
      setEventMaps((prev) => {
        const next = [...prev];
        const row = { ...next[idx] };
        const opening = !row.showExtra;
        row.showExtra = opening;
        if (opening && !(row.conditions || []).length) {
          row.conditions = [emptyCondition()];
        }
        next[idx] = row;
        return next;
      });
    });
  }

  function updateMapRow(idx, patch) {
    markDirty(() => {
      setEventMaps((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], ...patch };
        return next;
      });
    });
  }

  function updateCondition(idx, cIdx, patch) {
    markDirty(() => {
      setEventMaps((prev) => {
        const next = [...prev];
        const conditions = [...(next[idx].conditions || [])];
        conditions[cIdx] = { ...conditions[cIdx], ...patch };
        next[idx] = { ...next[idx], conditions };
        return next;
      });
    });
  }

  function addCondition(idx) {
    markDirty(() => {
      setEventMaps((prev) => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          conditions: [...(next[idx].conditions || []), emptyCondition()],
        };
        return next;
      });
    });
  }

  function removeCondition(idx, cIdx) {
    markDirty(() => {
      setEventMaps((prev) => {
        const next = [...prev];
        const conditions = (next[idx].conditions || []).filter((_, i) => i !== cIdx);
        next[idx] = { ...next[idx], conditions, showExtra: conditions.length > 0 };
        return next;
      });
    });
  }

  useEffect(() => {
    if (!selectedMapIdxs.length) setBulkOpen(false);
  }, [selectedMapIdxs.length]);

  useEffect(() => {
    if (!bulkOpen) return undefined;
    function onDocClick(e) {
      if (bulkRef.current && !bulkRef.current.contains(e.target)) setBulkOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [bulkOpen]);

  if (!loading && !partner) {
    return (
      <Navigate to={`/apps/${routeAppId}/collaborate/partner-integrations`} replace />
    );
  }

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner pi-detail-shell">
        {loading ? (
          <div className="pi-empty">Loading…</div>
        ) : (
          <div className="pi-detail-layout">
            <div className="pi-detail-top">
              <div className="pi-detail-header">
                <PartnerLogo partner={partner} className="pi-logo" />
                <div>
                  <h1>{partner.name}</h1>
                  <span
                    className={`pi-status ${status === 1 ? "pi-status--active" : ""}`}
                    style={{ marginTop: 8 }}
                  >
                    <span className="pi-status-dot" />
                    {status === 1 ? "Active" : "Not activated"}
                  </span>
                </div>
              </div>
            </div>

            <div className="pi-detail-tabs" role="tablist">
              <button
                type="button"
                className={`pi-tab ${tab === "integration" ? "active" : ""}`}
                onClick={() => setTab("integration")}
              >
                Integration
              </button>
              <button
                type="button"
                className={`pi-tab ${tab === "about" ? "active" : ""}`}
                onClick={() => setTab("about")}
              >
                About
              </button>
            </div>

            <div className="pi-detail-scroll">
              {tab === "about" ? (
                <div className="pi-section">
                  <h2>About {partner.name}</h2>
                  <p>{partner.description}</p>
                  {partner.about_text ? <p>{partner.about_text}</p> : null}
                  <p>
                    Type:{" "}
                    {partner.partner_type_label ||
                      PARTNER_TYPE.find((t) => t.value === Number(partner.partner_type))
                        ?.label}
                  </p>
                </div>
              ) : (
                <div className="pi-section">
                  <div className="pi-block">
                    <h2>{partner.name} integration</h2>
                    <p>Set up your integration details for this partner</p>
                    <label className="pi-check-label">
                      <input
                        type="checkbox"
                        checked={status === 1}
                        disabled={!canManage}
                        onChange={(e) => onActivateChange(e.target.checked)}
                      />
                      Activate partner
                    </label>
                    {status !== 1 ? (
                      <p style={{ marginTop: 12, color: "#6b7280" }}>
                        Activate this partner to configure default and in-app event postbacks.
                      </p>
                    ) : null}
                  </div>

                  {status === 1 ? (
                    <>
                      <div className="pi-block">
                        <h2>Default postbacks</h2>
                        <p>Select how to send the following postbacks to this partner.</p>
                        <div className="pi-map-table pi-map-table--default">
                          <div className="pi-map-head">
                            <span>Event name</span>
                            <span>for users from</span>
                          </div>
                          <div className="pi-map-row">
                            <input
                              type="text"
                              value="install"
                              disabled
                              readOnly
                              aria-label="Event name"
                            />
                            <Select
                              options={PARTNER_USERS_FROM}
                              value={
                                PARTNER_USERS_FROM.find((o) => o.value === Number(installUsersFrom)) ||
                                null
                              }
                              onChange={(opt) =>
                                markDirty(() => setInstallUsersFrom(Number(opt?.value)))
                              }
                              isDisabled={!canManage}
                              isSearchable
                              styles={mapSelectStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              placeholder="Select…"
                              aria-label="Install users from"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="pi-block">
                        <label className="pi-check-label pi-check-label--title">
                          <input
                            type="checkbox"
                            checked={inAppEnabled === 1}
                            disabled={!canManage}
                            onChange={(e) =>
                              markDirty(() => setInAppEnabled(e.target.checked ? 1 : 0))
                            }
                          />
                          <span>In-app event postbacks</span>
                        </label>

                        {inAppEnabled === 1 ? (
                          <>
                            <p>Set up the events and data included in postbacks sent to this partner</p>
                            <p>In-app event postback window</p>
                            <div className="pi-window-row">
                              {PARTNER_IN_APP_WINDOW_UNIT.map((u) => (
                                <label key={u.value} className="pi-radio">
                                  <input
                                    type="radio"
                                    name="window_unit"
                                    checked={windowUnit === u.value}
                                    disabled={!canManage}
                                    onChange={() => setWindowUnitSafe(u.value)}
                                  />
                                  {u.label}
                                </label>
                              ))}
                            </div>
                            {windowUnit !== 4 ? (
                              <div className="pi-window-value">
                                <div className="pi-window-slider">
                                  <input
                                    type="range"
                                    min={1}
                                    max={windowUnitMax(windowUnit)}
                                    value={Math.min(windowValue, windowUnitMax(windowUnit))}
                                    disabled={!canManage}
                                    onChange={(e) =>
                                      markDirty(() => setWindowValue(Number(e.target.value)))
                                    }
                                    list={`pi-window-ticks-${windowUnit}`}
                                  />
                                  <div className="pi-window-ticks" aria-hidden>
                                    {windowTickLabels(windowUnit).map((t) => (
                                      <span key={t}>{t}</span>
                                    ))}
                                  </div>
                                  <datalist id={`pi-window-ticks-${windowUnit}`}>
                                    {windowTickLabels(windowUnit).map((t) => (
                                      <option key={t} value={t} />
                                    ))}
                                  </datalist>
                                </div>
                                <span className="pi-window-readout">
                                  &gt; {windowUnitLabel(windowUnit, windowValue)}
                                </span>
                              </div>
                            ) : null}

                            <div className="pi-map-toolbar">
                              <div className="pi-bulk-wrap" ref={bulkRef}>
                                <button
                                  type="button"
                                  className="pi-bulk-btn"
                                  disabled={!canManage || selectedMapIdxs.length === 0}
                                  onClick={() => setBulkOpen((o) => !o)}
                                >
                                  Bulk actions ▾
                                </button>
                                {bulkOpen ? (
                                  <div className="pi-bulk-menu">
                                    <div className="pi-bulk-group">
                                      <div className="pi-bulk-group-title">for users from</div>
                                      {PARTNER_USERS_FROM.map((o) => (
                                        <label key={o.value} className="pi-bulk-option">
                                          <input
                                            type="radio"
                                            name="bulk_users_from"
                                            checked={bulkUsersFrom === o.value}
                                            onChange={() => setBulkUsersFrom(o.value)}
                                          />
                                          {o.label}
                                        </label>
                                      ))}
                                    </div>
                                    <div className="pi-bulk-group">
                                      <div className="pi-bulk-group-title">including</div>
                                      {PARTNER_INCLUDE_VALUES.map((o) => (
                                        <label key={o.value} className="pi-bulk-option">
                                          <input
                                            type="radio"
                                            name="bulk_include"
                                            checked={bulkInclude === o.value}
                                            onChange={() => setBulkInclude(o.value)}
                                          />
                                          {o.label}
                                        </label>
                                      ))}
                                    </div>
                                    <button
                                      type="button"
                                      className="pi-btn-primary pi-bulk-confirm"
                                      disabled={!selectedMapIdxs.length}
                                      onClick={applyBulkActions}
                                    >
                                      Confirm
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            <div className="pi-map-table pi-map-table--events" style={{ marginTop: 8 }}>
                              <div className="pi-map-head">
                                <span>
                                  <input
                                    type="checkbox"
                                    checked={
                                      eventMaps.length > 0 &&
                                      selectedMapIdxs.length === eventMaps.length
                                    }
                                    disabled={!canManage || !eventMaps.length}
                                    onChange={toggleSelectAllMaps}
                                    aria-label="Select all events"
                                  />
                                </span>
                                <span>NotifySphere event</span>
                                <span>mapped to partner event</span>
                                <span>for users from</span>
                                <span>including</span>
                                <span />
                              </div>
                              {eventMaps.length === 0 ? (
                                <p className="pi-map-empty">
                                  No mapped events yet. Use Add event below.
                                </p>
                              ) : (
                                eventMaps.map((row, idx) => (
                                  <div className="pi-map-event" key={`map-${idx}`}>
                                    <div className="pi-map-row">
                                      <input
                                        type="checkbox"
                                        checked={selectedMapIdxs.includes(idx)}
                                        disabled={!canManage}
                                        onChange={() => toggleMapSelected(idx)}
                                        aria-label={`Select event row ${idx + 1}`}
                                      />
                                      <CreatableSelect
                                        className="pi-map-creatable"
                                        options={nsEventOptions}
                                        value={
                                          row.ns_event
                                            ? { value: row.ns_event, label: row.ns_event }
                                            : null
                                        }
                                        onChange={(opt) =>
                                          updateMapRow(idx, { ns_event: opt?.value || "" })
                                        }
                                        placeholder="Select or type event"
                                        isDisabled={!canManage}
                                        isClearable
                                        isSearchable
                                        styles={mapSelectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        formatCreateLabel={(input) => `Use "${input}"`}
                                      />
                                      <div className="pi-map-partner-cell">
                                        <span className="pi-map-chevron" aria-hidden>
                                          ›
                                        </span>
                                        {row.showExtra ? (
                                          <input
                                            type="text"
                                            value="Based on conditions"
                                            disabled
                                            readOnly
                                          />
                                        ) : (
                                          <CreatableSelect
                                            className="pi-map-creatable"
                                            options={partnerEventOptions}
                                            value={
                                              row.partner_event
                                                ? {
                                                    value: row.partner_event,
                                                    label: row.partner_event,
                                                  }
                                                : null
                                            }
                                            onChange={(opt) =>
                                              updateMapRow(idx, {
                                                partner_event: opt?.value || "",
                                              })
                                            }
                                            placeholder="Select or type partner event"
                                            isDisabled={!canManage}
                                            isClearable
                                            isSearchable
                                            styles={mapSelectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            formatCreateLabel={(input) => `Use "${input}"`}
                                          />
                                        )}
                                      </div>
                                      <Select
                                        options={PARTNER_USERS_FROM}
                                        value={
                                          PARTNER_USERS_FROM.find(
                                            (o) => o.value === Number(row.users_from)
                                          ) || null
                                        }
                                        onChange={(opt) =>
                                          updateMapRow(idx, {
                                            users_from: Number(opt?.value),
                                          })
                                        }
                                        isDisabled={!canManage}
                                        isSearchable
                                        styles={mapSelectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        placeholder="Select…"
                                        aria-label="Users from"
                                      />
                                      <Select
                                        options={PARTNER_INCLUDE_VALUES}
                                        value={
                                          PARTNER_INCLUDE_VALUES.find(
                                            (o) => o.value === Number(row.include_values)
                                          ) || null
                                        }
                                        onChange={(opt) =>
                                          updateMapRow(idx, {
                                            include_values: Number(opt?.value),
                                          })
                                        }
                                        isDisabled={!canManage}
                                        isSearchable
                                        styles={mapSelectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        placeholder="Select…"
                                        aria-label="Including"
                                      />
                                      <div className="pi-map-actions">
                                        <button
                                          type="button"
                                          className="pi-icon-btn"
                                          disabled={!canManage}
                                          onClick={() => toggleExtraFields(idx)}
                                          title="Additional fields"
                                          aria-label="Additional fields"
                                        >
                                          <MdPlaylistAdd size={20} />
                                        </button>
                                        <button
                                          type="button"
                                          className="pi-icon-btn pi-icon-btn--danger"
                                          disabled={!canManage}
                                          onClick={() =>
                                            markDirty(() => {
                                              setEventMaps(eventMaps.filter((_, i) => i !== idx));
                                              setSelectedMapIdxs((prev) =>
                                                prev
                                                  .filter((i) => i !== idx)
                                                  .map((i) => (i > idx ? i - 1 : i))
                                              );
                                            })
                                          }
                                          title="Remove"
                                          aria-label="Remove event"
                                        >
                                          <MdDelete size={18} />
                                        </button>
                                      </div>
                                    </div>

                                    {row.showExtra ? (
                                      <div className="pi-conditions">
                                        {(row.conditions || []).map((cond, cIdx) => (
                                          <div className="pi-condition-row" key={`c-${idx}-${cIdx}`}>
                                            <span className="pi-condition-when">When</span>
                                            <input
                                              placeholder="Property"
                                              value={cond.property}
                                              disabled={!canManage}
                                              onChange={(e) =>
                                                updateCondition(idx, cIdx, {
                                                  property: e.target.value,
                                                })
                                              }
                                            />
                                            <span>equals</span>
                                            <input
                                              placeholder="Value"
                                              value={cond.value}
                                              disabled={!canManage}
                                              onChange={(e) =>
                                                updateCondition(idx, cIdx, {
                                                  value: e.target.value,
                                                })
                                              }
                                            />
                                            <span>map to</span>
                                            <CreatableSelect
                                              className="pi-map-creatable pi-map-creatable--cond"
                                              options={partnerEventOptions}
                                              value={
                                                cond.map_to
                                                  ? { value: cond.map_to, label: cond.map_to }
                                                  : null
                                              }
                                              onChange={(opt) =>
                                                updateCondition(idx, cIdx, {
                                                  map_to: opt?.value || "",
                                                })
                                              }
                                              placeholder="Select or type partner event"
                                              isDisabled={!canManage}
                                              isClearable
                                              isSearchable
                                              styles={mapSelectStyles}
                                              menuPortalTarget={document.body}
                                              menuPosition="fixed"
                                              formatCreateLabel={(input) => `Use "${input}"`}
                                            />
                                            <button
                                              type="button"
                                              className="pi-icon-btn pi-icon-btn--danger"
                                              disabled={!canManage}
                                              onClick={() => removeCondition(idx, cIdx)}
                                              aria-label="Remove condition"
                                            >
                                              <MdDelete size={18} />
                                            </button>
                                          </div>
                                        ))}
                                        {canManage ? (
                                          <button
                                            type="button"
                                            className="pi-add-condition"
                                            onClick={() => addCondition(idx)}
                                          >
                                            + Add condition
                                          </button>
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </div>
                                ))
                              )}
                            </div>

                            {canManage ? (
                              <button
                                type="button"
                                className="pi-add-event"
                                onClick={() =>
                                  markDirty(() => {
                                    setEventMaps([...eventMaps, emptyMapRow()]);
                                  })
                                }
                              >
                                + Add event
                              </button>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </div>
              )}
            </div>

            <div className="pi-footer-bar">
              <Link
                className="pi-detail-back"
                to={`/apps/${routeAppId}/collaborate/partner-integrations`}
              >
                ← Back to list
              </Link>
              {tab === "integration" ? (
                <div className="pi-footer-actions">
                  {dirty ? (
                    <span className="pi-unsaved">You have unsaved changes</span>
                  ) : null}
                  <button
                    type="button"
                    className="pi-btn-ghost"
                    disabled={!dirty || saving}
                    onClick={discardChanges}
                  >
                    Discard changes
                  </button>
                  <button
                    type="button"
                    className="pi-btn-primary"
                    disabled={!canManage || saving || !dirty}
                    onClick={handleSave}
                  >
                    {saving ? "Saving…" : "Save Integration"}
                  </button>
                </div>
              ) : (
                <span />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
